import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import CryptoJS from "crypto-js";
import { sessionState } from "../models/states/useSession";
import {
  APP_PRIVATE_KEY,
  APP_SECRET_KEY,
  REACT_APP_ENCRYPTION_SPLITER,
  THEME,
} from "../constants/Settings";
import { divide, multiply, mapValues } from "lodash";
import { TStateApi } from "zustand-x";
import React from "react";
import { lazyRetry } from "../utils/lazy-retry";

dayjs.extend(utc);
dayjs.extend(timezone);

export function aes_encrypt({ data }: { data: string }) {
  const sessionId = sessionState().session_id;

  const splitter = REACT_APP_ENCRYPTION_SPLITER;
  const parsedKey = CryptoJS.enc.Hex.parse(APP_SECRET_KEY);
  const parsedIv = CryptoJS.enc.Hex.parse(
    `${APP_PRIVATE_KEY}${CryptoJS.SHA256(sessionId as any)
      .toString()
      .substring(0, 16)}`
  );

  const chipperText = CryptoJS.AES.encrypt(data, parsedKey, {
    iv: parsedIv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();

  return `${CryptoJS.SHA256(sessionId as any)
    .toString()
    .substring(0, 16)}${splitter}${chipperText}`;
}

export function aes_decrypt({
  data,
  secretKey,
}: {
  data: string;
  secretKey?: string;
}) {
  const splitter = REACT_APP_ENCRYPTION_SPLITER;
  const [sessionId, chipperText] = data?.split(splitter);

  const parsedKey = CryptoJS.enc.Hex.parse(secretKey ?? APP_SECRET_KEY);
  const parsedIv = CryptoJS.enc.Hex.parse(`${APP_PRIVATE_KEY}${sessionId}`);

  const decrypted = CryptoJS.AES.decrypt(chipperText, parsedKey, {
    iv: parsedIv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return decrypted.toString(CryptoJS.enc.Utf8);
}

export function format_date(date: string, format = "DD MMM, YYYY") {
  return dayjs.tz(date, dayjs.tz.guess()).format(format);
}

export function get_search_params(params: any) {
  const allSearchParams = Object.fromEntries([...params]);

  return allSearchParams;
}

export function format_date_time(date: string) {
  return dayjs(date).format("DD MMM YYYY, hh:mm A");
}
export function format_time(date: string, format = "DD MMM, YYYY") {
  return dayjs(date).format("hh:mm A");
}
export function format_currency(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ?? "USD",
  }).format(value);
}

export function format_number(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatNumberAbbreviated(num: number) {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
  }).format(num);
}

export function getTimeOffset() {
  const date = new Date();
  const tz = dayjs.tz.guess(); // Example timezone with +05:30 offset

  // Get timezone offset in minutes
  const offsetInMinutes = dayjs.tz(date, tz).utcOffset();

  // Format as ±HH:mm
  const offsetHours = Math.floor(Math.abs(offsetInMinutes) / 60);
  const offsetMinutes = Math.abs(offsetInMinutes % 60);
  const sign = offsetInMinutes >= 0 ? "+" : "-";
  const formattedOffset = `${sign}${String(offsetHours).padStart(
    2,
    "0"
  )}:${String(offsetMinutes).padStart(2, "0")}`;

  return formattedOffset;
}

export function isEmpty(value: any) {
  return (
    value == null || (typeof value === "string" && value.trim().length === 0)
  );
}

export function isJSON(str: any) {
  try {
    return JSON.parse(str) && !!str;
  } catch (e) {
    return false;
  }
}

/**
 * Checks if a URL string starts with http:// or https:// protocol
 * @param url The URL string to check
 * @returns boolean indicating if the URL has a valid http/https protocol
 */
export const hasHttpProtocol = (url: string): boolean => {
  if (!url) return false;
  const protocolPattern = /^https?:\/\//;
  return protocolPattern.test(url);
};

export function calculateCommissionOut(
  amount: number,
  commissionPercentage: number
) {
  const commissionAmount = multiply(amount, divide(commissionPercentage, 100));

  return commissionAmount;
}

export function calculateRevenueDifference(
  currentMonth: number,
  lastMonth: number
) {
  // Check if lastMonth is zero to avoid division by zero
  if (lastMonth === 0) {
    return currentMonth > 0 ? 100 : 0;
    // isPositive: currentMonth >= 0,
  }

  // Calculate the percentage difference
  const difference = currentMonth - lastMonth;
  const percentageDifference = (difference / lastMonth) * 100;

  return Math.abs(percentageDifference).toFixed(2);
  // isPositive: percentageDifference >= 0,
}

export function positiveNegativeIcon(
  currentMonth: number,
  lastMonth: number
): boolean {
  // Check if lastMonth is zero to avoid division by zero
  if (lastMonth === 0) {
    return currentMonth >= 0;
  }

  // Calculate the percentage difference
  const difference = currentMonth - lastMonth;
  const percentageDifference = (difference / lastMonth) * 100;

  return percentageDifference >= 0;
}

export function decodeJWTPayload(token: string) {
  const base64Url = token.split(".")[1]; // Get the payload part
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/"); // Replace URL-safe characters
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );

  return JSON.parse(jsonPayload);
}

export const mapValuesKey = <
  VK extends keyof TStateApi<any, any, any, any>,
  T extends Record<VK, any>,
  R extends Record<keyof R, T>
>(
  key: VK,
  obj: R
) => mapValues(obj, (value) => value[key]) as { [K in keyof R]: R[K][VK] };

// export const createLazyComponent = (pageName: string) =>
//   React.lazy(() =>
//     lazyRetry(
//       () => import(`../themes/${THEME}/pages/${pageName}/${pageName}.page`),
//       pageName
//     )
//   );

export const createLazyComponent = (relativePath: string) =>
  React.lazy(() =>
    lazyRetry(
      () => import(`../themes/${THEME}/pages/${relativePath}`),
      relativePath
    )
  );
